import { FC, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { DataList, InputWithSearchIcon, ToggleSwitch, noRowsDefault } from 'common/components';
import { ACTION_ITEMS, SEARCH_DEBOUNCE_DELAY } from 'common/config';
import { ApiStatus } from 'common/enums';
import { PagedRequest } from 'common/models/data/pagedRequest.model';
import useDebounce from 'common/hooks/useDebounce';
import { RootState } from 'common/redux/core/root.reducer';

import { FILE_UPLOAD_DATA_TITLE, getUploadFilesColDefs } from 'modules/configuration/config';
import { configurationActions } from 'modules/configuration/redux';
import { getFileUploadLogDataResponse } from 'modules/configuration/redux/configuration.selector';

import './UploadListPage.scss';
import useSyncHistoryStatusAccess from 'modules/sync-history/role-access-hooks/useSyncHistoryAccess';

const UploadListPage: FC = () => {
    const [isMine, setIsMine] = useState(false);
    const [searchVal, setSearchVal] = useState<string>('');
    const [pagedRequest, setPagedRequest] = useState<PagedRequest>({
        pageNumber: 1,
        pageSize: 20,
        searchText: '',
        isMine: isMine
    });
    const fileUploadLogResponse = useSelector((state: RootState) =>
        getFileUploadLogDataResponse(state)
    );

    //const { isUploadHistoryStatusDisabled } = useSyncHistoryStatusAccess();
    const dispatch = useDispatch();

    /**
     * To handle infinite scroll
     */
    const handleScroll = () => {
        if (
            fileUploadLogResponse.status !== ApiStatus.LOADING &&
            fileUploadLogResponse.data?.pagination &&
            fileUploadLogResponse.data?.pagination.totalPages > pagedRequest.pageNumber
        ) {
            const updatedPagedRequest: PagedRequest = { ...pagedRequest };
            updatedPagedRequest.pageNumber = updatedPagedRequest.pageNumber + 1;
            updatedPagedRequest.searchText = searchVal;
            setPagedRequest(updatedPagedRequest);
            getUploadFileLog(updatedPagedRequest);
        }
    };

    /**
     * Following method is responsible for fetching upload file log.
     */
    const getUploadFileLog = useCallback(
        (pagedRequest: PagedRequest) => {
            dispatch(configurationActions.fetchUploadFileLogBegin(pagedRequest));
        },
        [dispatch]
    );

    useEffect(() => {
        getUploadFileLog(pagedRequest);
    }, [getUploadFileLog, pagedRequest]);

    /**
     * This function handle serach event
     * @param event Event information object created by user on search box
     */

    const onSearchChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchVal(event.target.value);
    };

    useDebounce(
        () => {
            if (searchVal.length >= 3 || searchVal.length === 0) {
                const updatedPagedRequest: PagedRequest = { ...pagedRequest };
                updatedPagedRequest.pageNumber = 1;
                updatedPagedRequest.searchText = searchVal;
                getUploadFileLog(updatedPagedRequest);
            }
        },
        [searchVal],
        SEARCH_DEBOUNCE_DELAY
    );

    return (
        <div className="route-container">
            <div className={`data-editor-header-container`}>
                <div className={`data-editor-header header-default`}>{FILE_UPLOAD_DATA_TITLE}</div>
                <div className="header-right">
                    <ToggleSwitch
                        className="toggle-switch"
                        id="myUploads"
                        checked={isMine}
                        name=""
                        hideLabels={false}
                        value={'myUpload'}
                        onLabel=""
                        offLabel={'My uploads'}
                        onChange={(event: any) => {
                            const updatedPagedRequest: PagedRequest = { ...pagedRequest };
                            if (isMine) {
                                setIsMine(false);
                                updatedPagedRequest.isMine = false;
                            } else {
                                setIsMine(true);
                                updatedPagedRequest.isMine = true;
                            }
                            updatedPagedRequest.pageNumber = 1;
                            updatedPagedRequest.searchText = searchVal;
                            setPagedRequest(updatedPagedRequest);
                        }}></ToggleSwitch>
                    <InputWithSearchIcon
                        placeholder={ACTION_ITEMS.SEARCH_BUTTON}
                        value={searchVal}
                        formClassName="search-input-wrapper"
                        className="search-input"
                        hideClearButton={false}
                        onChange={onSearchChangeHandler}></InputWithSearchIcon>
                </div>
            </div>
            <div className="upload-history-wrapper">
                <DataList
                    isNoDataVisible={fileUploadLogResponse?.data?.data?.length === 0}
                    data={fileUploadLogResponse?.data?.data ? fileUploadLogResponse.data.data : []}
                    noRowsTemplate={noRowsDefault}
                    fetchNextDataSet={handleScroll}
                    columnDefs={getUploadFilesColDefs()}
                />
            </div>
        </div>
    );
};

export default UploadListPage;
